/* Elite Fanstore - méga-menu ordinateur (assets/elite-mega-menu.js), chargé par snippets/elite-mega-menu.liquid.
   Souris : ouverture au survol (petit délai pour ne pas ouvrir en passant), fermeture en quittant barre + panneau.
   Tactile (tablette en paysage) : 1er appui sur une entrée = ouvre le panneau, 2e appui = va sur la page.
   Clavier : bouton flèche à côté de chaque entrée (aria-expanded), Échap ferme, flèches dans les onglets.
   Les onglets (championnats, continents...) changent au survol ou au focus ; un clic garde la navigation vers la page.
   Panneau ouvert : classe em-panel-open sur la section de l'en-tête, qui passe alors devant le contenu de la page
   (barre de filtres des collections, etc.).
   Contenu des panneaux livré dans un <template data-em-tpl> (inerte : la page se charge plus vite) et inséré
   dès que la souris entre dans la barre, au premier focus clavier ou à l'ouverture ; tout de suite dans l'éditeur. */
(() => {
  if (customElements.get('elite-mega-menu')) return;

  const OPEN_DELAY = 120;
  const SWITCH_DELAY = 40;
  const CLOSE_DELAY = 220;
  const TAB_DELAY = 90;

  class EliteMegaMenu extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-em-item]')];
      this.headerSection = this.closest('.section-header');
      this.current = null;
      this.timer = null;
      this.tabTimer = null;
      this.pointer = 'mouse';

      this.addEventListener('pointerdown', (e) => { this.pointer = e.pointerType || 'mouse'; }, true);

      this.items.forEach((item) => {
        const trigger = item.querySelector('[data-em-trigger]');
        const toggle = item.querySelector('[data-em-toggle]');
        const panel = item.querySelector('[data-em-panel]');
        if (!panel) return;

        item.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.schedule(() => this.open(item), this.current ? SWITCH_DELAY : OPEN_DELAY);
        });
        item.addEventListener('pointerleave', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.schedule(() => this.close(), CLOSE_DELAY);
        });

        if (trigger && trigger !== toggle) {
          trigger.addEventListener('click', (e) => {
            if (this.pointer !== 'mouse' && this.current !== item) {
              e.preventDefault();
              this.open(item);
            }
          });
        }
        if (toggle) {
          toggle.addEventListener('click', (e) => {
            e.preventDefault();
            if (this.current !== item) { this.open(item); return; }
            // déjà ouvert par le survol : un clic souris ne referme pas sous le curseur (clavier : e.detail === 0)
            if (this.pointer === 'mouse' && e.detail !== 0) return;
            this.close(toggle);
          });
        }

        panel.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowLeft') return;
          const group = e.target.closest('[data-em-group]');
          if (!group) return;
          const tab = panel.querySelector(`[data-em-tab="${group.id}"]`);
          if (tab) { e.preventDefault(); tab.focus(); }
        });
      });

      this.prepareAll = () => this.items.forEach((item) => {
        const panel = item.querySelector('[data-em-panel]');
        if (panel) this.prepare(panel);
      });
      this.addEventListener('pointerenter', this.prepareAll, { once: true });
      this.addEventListener('focusin', this.prepareAll, { once: true });
      if (window.Shopify && window.Shopify.designMode) this.prepareAll();

      this.onKey = (e) => {
        if (e.key !== 'Escape' || !this.current) return;
        const back = this.current.querySelector('[data-em-toggle]');
        this.close();
        if (back) back.focus();
      };
      this.onDocClick = (e) => {
        if (this.current && !this.contains(e.target)) this.close();
      };
      this.onFocusOut = (e) => {
        if (this.current && e.relatedTarget && !this.current.contains(e.relatedTarget)) this.close();
      };
      this.onScroll = () => {
        if (!this.current) return;
        if (Math.abs(window.scrollY - this.scrollAtOpen) > 60) this.close();
      };
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('click', this.onDocClick);
      this.addEventListener('focusout', this.onFocusOut);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('click', this.onDocClick);
      window.removeEventListener('scroll', this.onScroll);
    }

    // insère le contenu du panneau (une seule fois) et branche ses onglets
    prepare(panel) {
      const tpl = panel.querySelector(':scope > template[data-em-tpl]');
      if (!tpl) return;
      tpl.replaceWith(tpl.content.cloneNode(true));

      panel.querySelectorAll('[data-em-tab]').forEach((tab) => {
        tab.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          clearTimeout(this.tabTimer);
          this.tabTimer = setTimeout(() => this.activate(panel, tab), TAB_DELAY);
        });
        tab.addEventListener('pointerleave', () => clearTimeout(this.tabTimer));
        tab.addEventListener('focus', () => this.activate(panel, tab));
        tab.addEventListener('click', (e) => {
          if (this.pointer !== 'mouse' && !tab.classList.contains('is-active')) {
            e.preventDefault();
            this.activate(panel, tab);
          }
        });
        tab.addEventListener('keydown', (e) => this.onTabKey(e, panel, tab));
      });
    }

    schedule(fn, delay) {
      clearTimeout(this.timer);
      this.timer = setTimeout(fn, delay);
    }

    open(item) {
      clearTimeout(this.timer);
      if (this.current === item) return;
      if (this.current) this.close(null, true);
      const panel = item.querySelector('[data-em-panel]');
      this.prepare(panel);
      panel.hidden = false;
      item.classList.add('is-open');
      item.querySelectorAll('[data-em-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'true'));
      const top = item.closest('.header-wrapper, sticky-header, .section-header') || this;
      panel.style.setProperty('--em-panel-top', `${Math.max(0, top.getBoundingClientRect().bottom)}px`);
      if (this.headerSection) this.headerSection.classList.add('em-panel-open');
      this.current = item;
      this.scrollAtOpen = window.scrollY;
    }

    close(focusTarget, switching) {
      clearTimeout(this.timer);
      const item = this.current;
      if (!item) return;
      const panel = item.querySelector('[data-em-panel]');
      panel.hidden = true;
      item.classList.remove('is-open');
      item.querySelectorAll('[data-em-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
      if (this.headerSection) this.headerSection.classList.remove('em-panel-open');
      this.current = null;
      if (!switching) this.resetTabs(panel);
      if (focusTarget) focusTarget.focus();
    }

    activate(panel, tab) {
      if (tab.classList.contains('is-active')) return;
      panel.querySelectorAll('[data-em-tab]').forEach((t) => t.classList.toggle('is-active', t === tab));
      panel.querySelectorAll('[data-em-group]').forEach((g) => { g.hidden = g.id !== tab.dataset.emTab; });
    }

    resetTabs(panel) {
      const first = panel.querySelector('[data-em-tab]');
      if (first) this.activate(panel, first);
    }

    onTabKey(e, panel, tab) {
      const tabs = [...panel.querySelectorAll('[data-em-tab]')];
      const i = tabs.indexOf(tab);
      if (e.key === 'ArrowDown' && i < tabs.length - 1) { e.preventDefault(); tabs[i + 1].focus(); }
      else if (e.key === 'ArrowUp' && i > 0) { e.preventDefault(); tabs[i - 1].focus(); }
      else if (e.key === 'ArrowRight') {
        const first = panel.querySelector(`#${CSS.escape(tab.dataset.emTab)} a`);
        if (first) { e.preventDefault(); first.focus(); }
      }
    }
  }

  customElements.define('elite-mega-menu', EliteMegaMenu);
})();
